import { useMutation, useQuery } from '@tanstack/react-query';
import { ILandMarking } from '../../Interfaces/ILandMarking';
import { customFetcher } from '../useFetch';
import {
  ISearchOptionLandRegister,
  ISearchOptionLandRegisterResult,
} from '../../Interfaces/Helper/ISearchOptionLandRegister';
import { IRealEstateLandRegisterEntryQuery } from '../../Interfaces/IRealEstateLandRegisterEntryQuery';

export const useGetLandMarkings = () => {
  const { data: landMarkings, isLoading: isLoadingLandMarkings } = useQuery<
    ILandMarking[]
  >({
    queryKey: ['landmarking'],
    queryFn: () => customFetcher({ endpoint: '/landmarking' }),
  });

  return { landMarkings, isLoadingLandMarkings };
};

export const useTriggerLandRegisterDownload = () => {
  const {
    mutateAsync: downloadLandRegister,
    isPending: isDownloadingLandMarkings,
  } = useMutation<
    IRealEstateLandRegisterEntryQuery,
    Error,
    ISearchOptionLandRegister
  >({
    mutationFn: (legalClient: ISearchOptionLandRegister) =>
      customFetcher({
        endpoint: '/landregister/download',
        method: 'POST',
        body: legalClient,
      }),
  });

  return {
    downloadLandRegister,
    isDownloadingLandMarkings,
  };
};

//////////////////// Grundbuch-Nachverfolgung (manueller Trigger)
export interface ILandRegisterTrackingDueState {
  idFederalState: number;
  FederalState: string | null;
  dueCount: number;
}

export interface ILandRegisterTrackingDue {
  due: ILandRegisterTrackingDueState[];
  dueTotal: number;
  nextRecommendedCheck: string | null;
}

export interface ILandRegisterTrackingResultItem {
  idProcessManager: number;
  idProcessManagerType: number;
  idLegalTransaction: number | null;
  hasFound: boolean;
  error?: string | null;
}

export interface ILandRegisterTrackingResult {
  idFederalState: number;
  FederalState: string | null;
  skipped?: boolean;
  error?: string;
  dueCount?: number;
  processed?: number;
  found?: number;
  errors?: number;
  items?: ILandRegisterTrackingResultItem[];
}

export interface ILandRegisterTrackingRunResult {
  results: ILandRegisterTrackingResult[];
  nextRecommendedCheck: string | null;
}

export interface ILandRegisterTrackingCredential {
  idFederalState: number;
  idFederalStateLogin: number;
  password: string;
}

export const useGetLandRegisterTrackingDue = (enabled: boolean) => {
  const {
    data: trackingDue,
    isFetching: isLoadingTrackingDue,
    refetch: refetchTrackingDue,
  } = useQuery<ILandRegisterTrackingDue>({
    enabled,
    refetchOnWindowFocus: false,
    queryKey: ['landregister', 'tracking', 'due'],
    queryFn: () => customFetcher({ endpoint: '/landregister/tracking/due' }),
  });
  return { trackingDue, isLoadingTrackingDue, refetchTrackingDue };
};

export const useRunLandRegisterTracking = () => {
  const {
    mutateAsync: runLandRegisterTracking,
    isPending: isRunningLandRegisterTracking,
  } = useMutation<
    ILandRegisterTrackingRunResult,
    Error,
    ILandRegisterTrackingCredential[]
  >({
    mutationFn: (credentials: ILandRegisterTrackingCredential[]) =>
      customFetcher({
        endpoint: '/landregister/tracking/run',
        method: 'POST',
        body: { Credentials: credentials },
      }),
  });
  return { runLandRegisterTracking, isRunningLandRegisterTracking };
};

export const useSearchLandRegister = () => {
  const {
    mutateAsync: searchLandRegister,
    isPending: isSearchingLandRegister,
  } = useMutation<
    ISearchOptionLandRegisterResult[],
    Error,
    ISearchOptionLandRegister
  >({
    mutationFn: (legalClient: ISearchOptionLandRegister) =>
      customFetcher({
        endpoint: '/landregister/search',
        method: 'POST',
        body: legalClient,
      }),
  });

  return {
    searchLandRegister,
    isSearchingLandRegister,
  };
};
